// Phase 83p — Reusable PickLeague brand <Logo> component.
// Renders 1 of 3 variants. All variants ship pre-sized + alpha so the browser
// decodes them quickly. Use `priority` to skip lazy-loading on critical paths.
//
// v17.perf.auth-storm (02/2026) — HAR PROD cho thấy 5x fetch `/api/branding`
// mỗi phiên do có nhiều Logo instance (header, footer, splash, …). Dedupe
// bằng module-level promise cache: mọi Logo share chung 1 network request.
import { asset } from "../../lib/asset";
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../../lib/api";

const VARIANTS = {
  mark: { src: asset("/branding/logo-mark.png"), srcSm: asset("/branding/logo-mark-96.png"), srcLg: asset("/branding/logo-mark-512.png") },
  full: { src: asset("/branding/logo-full.png") },
  wordmark: { src: asset("/branding/logo-wordmark.png"), srcSm: asset("/branding/logo-wordmark-240.png") },
};

// Module-level cache — chỉ 1 request /api/branding cho toàn bộ app lifetime.
let _brandingPromise = null;
function fetchBrandingOnce() {
  if (_brandingPromise) return _brandingPromise;
  _brandingPromise = fetch(`${BACKEND_URL}/api/branding`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  return _brandingPromise;
}

export default function Logo({
  variant = "mark",      // 'mark' | 'full' | 'wordmark'
  size = 40,             // pixel height (logo width follows aspect ratio)
  className = "",
  priority = false,
  alt = "PickLeague",
  testid,
}) {
  // Phase 83p Phase 2 — pull custom branding overrides from /api/branding
  const [overrideSrc, setOverrideSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchBrandingOnce().then((cfg) => {
      if (!alive || !cfg) return;
      const u = cfg.logos && cfg.logos[variant];
      if (u) setOverrideSrc(u);
    });
    return () => { alive = false; };
  }, [variant]);
  const v = VARIANTS[variant] || VARIANTS.mark;
  const fallback = overrideSrc || v.src;
  // For mark variant: choose the smallest texture that still looks crisp
  const src = (() => {
    if (overrideSrc) return overrideSrc;
    if (variant === "mark" && size <= 64 && v.srcSm) return v.srcSm;
    if (variant === "mark" && size >= 256 && v.srcLg) return v.srcLg;
    if (variant === "wordmark" && size <= 60 && v.srcSm) return v.srcSm;
    return v.src;
  })();
  // Aspect ratios baked in
  const ratios = { mark: 1, full: 1, wordmark: 480 / 120 };
  const width = Math.round(size * (ratios[variant] || 1));
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={size}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      className={`select-none ${className}`}
      data-testid={testid || `logo-${variant}`}
      draggable={false}
      onError={(e) => {
        if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
      }}
    />
  );
}
